/*
  DB Dog Breeds Hub (Blog Index)
  - Search + Filters
  - Client-side pagination
  - URL-persisted state (filters, search, per-page, current page)
  - Mobile bottom-sheet filter panel
  - Active filter chips

  Performance note:
  This version virtualizes the grid (renders only the current page of cards)
  and consumes a JSON dataset rendered by Liquid. This removes ~250+ cards/images
  from the initial HTML, significantly improving TTFB, DOM parse, layout, and
  memory pressure on mobile.
*/

(function () {
  'use strict';

  var PARAMS = {
    page: 'dbpage',
    per: 'dbper',
    q: 'dbq',
    filterPrefix: 'dbf_',
  };

  var STORAGE_KEYS = {
    hubUrl: 'DB_DOG_BREEDS_HUB_URL',
    restoreFlag: 'DB_DOG_BREEDS_HUB_RESTORE',
    restoreUrlKey: 'DB_DOG_BREEDS_HUB_RESTORE_URLKEY',
    restoreScrollY: 'DB_DOG_BREEDS_HUB_RESTORE_SCROLLY',
    restoreAnchorHref: 'DB_DOG_BREEDS_HUB_RESTORE_ANCHOR_HREF',
    restoreAnchorTop: 'DB_DOG_BREEDS_HUB_RESTORE_ANCHOR_TOP',
  };

  var hubs = document.querySelectorAll('[data-dog-breeds-hub]');
  if (!hubs.length) return;

  function prefersReducedMotion() {
    if (!('matchMedia' in window)) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isPlainLeftClick(e) {
    if (!e) return false;
    if ('button' in e && e.button !== 0) return false;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
    return true;
  }

  function safeSessionGet(key) {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.setItem(key, String(value));
    } catch (e) {}
  }

  function safeSessionRemove(key) {
    try {
      if (!window.sessionStorage) return;
      window.sessionStorage.removeItem(key);
    } catch (e) {}
  }

  function normalize(s) {
    return (s || '').toString().trim().toLowerCase();
  }

  function toNumber(v) {
    if (v == null) return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    var n = parseFloat(String(v));
    return isFinite(n) ? n : null;
  }

  function clampInt(n, min, max) {
    var x = parseInt(n, 10);
    if (!isFinite(x)) return min;
    if (x < min) return min;
    if (max != null && x > max) return max;
    return x;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  hubs.forEach(function (root) {
    var grid = root.querySelector('[data-db-grid]');
    if (!grid) return;

    // ---- Dataset (JSON rendered by Liquid) ----
    var jsonEl = root.querySelector('[data-db-breeds-json]') || grid.querySelector('[data-db-breeds-json]');
    var breeds = [];
    var breedKeyIndex = {};

    function ingestBreeds(rawList) {
      if (!rawList || !rawList.length) return 0;

      var added = 0;
      rawList.forEach(function (it) {
        if (!it || typeof it !== 'object') return;

        var href = typeof it.href === 'string' ? it.href.trim() : '';
        if (!href) return;
        if (breedKeyIndex[href]) return;
        breedKeyIndex[href] = true;

        var title = typeof it.title === 'string' ? it.title : '';
        var excerpt = typeof it.excerpt === 'string' ? it.excerpt : '';

        var img = null;
        if (it.img && typeof it.img === 'object' && it.img.src) {
          img = {
            src: String(it.img.src),
            alt: String(it.img.alt || title || ''),
          };
        }

        breeds.push({
          href: href,
          title: title,
          excerpt: excerpt,
          titleNorm: normalize(title),
          excerptNorm: normalize(excerpt),
          img: img,

          height: toNumber(it.height),
          weight: toNumber(it.weight),
          lifespan: toNumber(it.lifespan),

          grade_apartment_dog: toNumber(it.grade_apartment_dog),
          grade_calmness: toNumber(it.grade_calmness),
          grade_grooming_clean_home: toNumber(it.grade_grooming_clean_home),
          grade_energy_activity: toNumber(it.grade_energy_activity),
          grade_family_dog: toNumber(it.grade_family_dog),
          grade_guard_watch: toNumber(it.grade_guard_watch),
          grade_smart: toNumber(it.grade_smart),
          grade_children: toNumber(it.grade_children),
          grade_multi_pet: toNumber(it.grade_multi_pet),
          grade_training: toNumber(it.grade_training),
        });

        added++;
      });

      return added;
    }

    function sortBreeds() {
      breeds.sort(function (a, b) {
        var ta = a.titleNorm || '';
        var tb = b.titleNorm || '';
        if (ta < tb) return -1;
        if (ta > tb) return 1;
        return 0;
      });
    }

    if (jsonEl) {
      try {
        var raw = JSON.parse(jsonEl.textContent || '[]');
        ingestBreeds(raw);
      } catch (e) {
        console.warn('[DB] Failed to parse breeds JSON:', e);
      }
    }

    sortBreeds();

    // ---- UI elements ----
    var noMatches = root.querySelector('[data-db-no-matches]');
    var loadingStatus = root.querySelector('[data-db-loading-status]');
    var loadMoreWrap = root.querySelector('[data-db-load-more-wrap]');
    var loadMoreBtn = root.querySelector('[data-db-load-more]');
    var loadMoreStep = Math.max(1, parseInt(grid.getAttribute('data-db-load-more-step') || '12', 10) || 12);
    var loadMoreLabel = (grid.getAttribute('data-db-load-more-label') || 'Load more breeds').trim() || 'Load more breeds';

    var matchCountEls = Array.prototype.slice.call(root.querySelectorAll('[data-db-match-count]'));
    var shownCountEls = Array.prototype.slice.call(root.querySelectorAll('[data-db-shown-count]'));

    var searchWrap = root.querySelector('[data-db-search]');
    var input = searchWrap ? searchWrap.querySelector('[data-db-search-input]') : null;
    var clearSearchBtn = searchWrap ? searchWrap.querySelector('[data-db-search-clear]') : null;

    var filtersPanel = root.querySelector('[data-db-filters]');
    var filtersBody = root.querySelector('[data-db-filters-body]');
    var overlay = root.querySelector('[data-db-filters-overlay]');
    var openFiltersBtn = root.querySelector('[data-db-filters-open]');
    var closeFiltersBtns = Array.prototype.slice.call(root.querySelectorAll('[data-db-filters-close]'));

    var clearFiltersBtns = Array.prototype.slice.call(root.querySelectorAll('[data-db-clear-filters]'));

    var activeWrap = root.querySelector('[data-db-active-filters]');
    var activeList = root.querySelector('[data-db-active-filters-list]');

    var perPageSelect = root.querySelector('[data-db-per-page]');

    var pagerWrap = root.querySelector('[data-db-pager]');
    var pagerNav = root.querySelector('[data-db-pagination]');

    var toolbar = root.querySelector('[data-db-toolbar]');

    var ALL_PER_PAGE = 99999;
    var numericPerPageOptions = [];
    var hasAllPerPageOption = false;

    if (perPageSelect) {
      Array.prototype.slice.call(perPageSelect.options || []).forEach(function (opt) {
        var value = (opt.value || '').trim().toLowerCase();
        if (value === 'all') {
          hasAllPerPageOption = true;
          return;
        }
        var n = parseInt(value, 10);
        if (isFinite(n) && n > 0 && numericPerPageOptions.indexOf(n) === -1) {
          numericPerPageOptions.push(n);
        }
      });
    }

    if (!numericPerPageOptions.length) numericPerPageOptions = [12, 24, 36, 48];
    numericPerPageOptions.sort(function (a, b) {
      return a - b;
    });

    var defaultPerPage = Math.max(1, parseInt(grid.getAttribute('data-db-initial') || '12', 10));
    if (numericPerPageOptions.indexOf(defaultPerPage) === -1) defaultPerPage = numericPerPageOptions[0] || 12;

    var perPage = defaultPerPage;
    var perPageMode = 'number';
    var currentPage = 1;

    // If URL requests a page that only exists after we load additional server pages,
    // store it here and retry once the dataset is expanded.
    var pendingPage = null;

    function isAllPerPage() {
      return perPageMode === 'all';
    }

    function ensurePerPageOption(value) {
      if (!perPageSelect || value == null) return;
      var stringValue = String(value);
      var exists = Array.prototype.slice.call(perPageSelect.options || []).some(function (opt) {
        return opt.value === stringValue;
      });
      if (exists) return;

      var option = document.createElement('option');
      option.value = stringValue;
      option.textContent = stringValue;
      option.setAttribute('data-db-dynamic-option', 'true');

      var allOption = hasAllPerPageOption ? perPageSelect.querySelector('option[value="all"]') : null;
      if (allOption) perPageSelect.insertBefore(option, allOption);
      else perPageSelect.appendChild(option);
    }

    function setPerPageFromValue(rawValue) {
      var raw = String(rawValue == null ? '' : rawValue).trim().toLowerCase();
      if (raw === 'all' && hasAllPerPageOption) {
        perPageMode = 'all';
        perPage = ALL_PER_PAGE;
        return true;
      }

      var parsed = parseInt(raw, 10);
      if (isFinite(parsed) && parsed > 0) {
        perPageMode = 'number';
        perPage = Math.min(parsed, ALL_PER_PAGE);
        ensurePerPageOption(perPage);
        return true;
      }

      perPageMode = 'number';
      perPage = defaultPerPage;
      ensurePerPageOption(perPage);
      return false;
    }

    function syncPerPageSelect() {
      if (!perPageSelect) return;
      if (isAllPerPage() && hasAllPerPageOption) {
        perPageSelect.value = 'all';
        return;
      }
      ensurePerPageOption(perPage);
      perPageSelect.value = String(perPage);
      if (!perPageSelect.value) perPageSelect.value = String(defaultPerPage);
    }

    // Initialize per-page dropdown to a valid value.
    setPerPageFromValue(defaultPerPage);
    syncPerPageSelect();

    // ---- Shopify server paging info for the initial data batch ----
    var serverTotalPages = parseInt(grid.getAttribute('data-db-server-pages') || '1', 10);
    if (!isFinite(serverTotalPages) || serverTotalPages < 1) serverTotalPages = 1;

    var serverCurrentPage = parseInt(grid.getAttribute('data-db-server-current') || '1', 10);
    if (!isFinite(serverCurrentPage) || serverCurrentPage < 1) serverCurrentPage = 1;

    var serverTotal = parseInt(grid.getAttribute('data-db-server-total') || String(breeds.length), 10);
    if (!isFinite(serverTotal) || serverTotal < breeds.length) serverTotal = breeds.length;

    var remoteRootId = (grid.getAttribute('data-db-root-id') || '').trim();
    var sectionRenderId = remoteRootId ? remoteRootId.replace(/^DogBreedsHub-/, '') : '';
    var abortServerLoad = false;
    var loadPromise = null;

    function hasMoreServerData() {
      return serverTotalPages > 1 && serverTotal > breeds.length;
    }

    function kickLoadAllBreeds() {
      if (serverTotalPages <= 1 || !hasMoreServerData()) return Promise.resolve();
      if (loadPromise) return loadPromise;

      setLoadingStatus(true, 'Loading the full breed list…');
      loadPromise = loadAllBreedsAcrossPages().then(function () {
        setLoadingStatus(false);
        if (hasMoreServerData()) loadPromise = null;
      });
      return loadPromise;
    }

    // ---- Filters: build lookup so we can restore via URL ----
    var allFilterInputs = [];
    var filterGroupNames = [];
    var groupInputs = {}; // group -> [inputs]
    var groupTokenIndex = {}; // group -> { token: input }
    var FILTER_VALUE_KEYS = {
      height: 'height',
      weight: 'weight',
      lifespan: 'lifespan',
      apartment_dog: 'grade_apartment_dog',
      calmness: 'grade_calmness',
      grooming_clean_home: 'grade_grooming_clean_home',
      energy_activity: 'grade_energy_activity',
      family_dog: 'grade_family_dog',
      guard_watch: 'grade_guard_watch',
      smart: 'grade_smart',
      children: 'grade_children',
      multi_pet: 'grade_multi_pet',
      training: 'grade_training',
    };

    function optionToken(inputEl) {
      if (!inputEl || !inputEl.getAttribute) return '';
      var min = (inputEl.getAttribute('data-min') || '').trim();
      var max = (inputEl.getAttribute('data-max') || '').trim();
      if (!min || !max) return '';
      var minExclusive = inputEl.getAttribute('data-min-exclusive') === 'true';
      return (minExclusive ? 'x' : 'i') + min + '_' + max;
    }

    if (filtersPanel) {
      allFilterInputs = Array.prototype.slice.call(
        filtersPanel.querySelectorAll('input[type="checkbox"][data-db-filter-group]')
      );

      allFilterInputs.forEach(function (inp) {
        var g = (inp.getAttribute('data-db-filter-group') || '').trim();
        if (!g) return;

        if (!groupInputs[g]) {
          groupInputs[g] = [];
          groupTokenIndex[g] = {};
          filterGroupNames.push(g);
        }

        groupInputs[g].push(inp);

        inp._dbGroup = g;

        // Pre-parse for performance in apply().
        inp._dbMin = toNumber(inp.getAttribute('data-min'));
        inp._dbMax = toNumber(inp.getAttribute('data-max'));
        inp._dbMinExclusive = inp.getAttribute('data-min-exclusive') === 'true';

        var t = optionToken(inp);
        if (t) {
          inp._dbToken = t;
          groupTokenIndex[g][t] = inp;
        }
      });
    }

    // ---- UI helpers ----
    function setNoMatches(show) {
      if (!noMatches) return;
      noMatches.hidden = !show;
    }

    function setCounts(matchCount, shownCount) {
      matchCountEls.forEach(function (el) {
        el.textContent = String(matchCount);
      });
      shownCountEls.forEach(function (el) {
        el.textContent = String(shownCount);
      });
    }

    function setLoadingStatus(show, message) {
      if (!loadingStatus) return;
      if (message) loadingStatus.textContent = message;
      loadingStatus.hidden = !show;
    }

    function selectedRangesHaveAny(selectedRanges) {
      if (!selectedRanges) return false;
      return Object.keys(selectedRanges).some(function (key) {
        return selectedRanges[key] && selectedRanges[key].length;
      });
    }

    function setClearButtonsEnabled(enabled) {
      clearFiltersBtns.forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !enabled;
        if (enabled) btn.removeAttribute('aria-disabled');
        else btn.setAttribute('aria-disabled', 'true');
      });
    }

    function getNextPerPageValue() {
      if (isAllPerPage()) return null;

      var nextNumeric = perPage + loadMoreStep;
      if (serverTotal && nextNumeric >= serverTotal && hasAllPerPageOption) return 'all';
      return nextNumeric;
    }

    function renderLoadMore(totalMatches, shownCount, waitingForData) {
      if (!loadMoreWrap || !loadMoreBtn) return;

      var hasMoreOnFirstPage = currentPage === 1 && totalMatches > shownCount;
      var canExpand = hasMoreOnFirstPage && (getNextPerPageValue() != null || isAllPerPage());
      var shouldShow = canExpand || (currentPage === 1 && waitingForData);

      loadMoreWrap.hidden = !shouldShow;
      if (!shouldShow) return;

      loadMoreBtn.disabled = !!waitingForData && isAllPerPage();
      loadMoreBtn.textContent = waitingForData ? 'Loading more breeds…' : loadMoreLabel;
    }

    function scrollToResultsTop() {
      var target = toolbar || grid;
      if (!target || typeof target.getBoundingClientRect !== 'function') return;

      var rect = target.getBoundingClientRect();
      var pageY = window.pageYOffset || window.scrollY || 0;
      var top = Math.max(0, rect.top + pageY - 14);

      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      try {
        window.scrollTo({ top: top, behavior: behavior });
      } catch (e) {
        window.scrollTo(0, top);
      }
    }

    function scrollToHubTop() {
      var target = root.querySelector('.db-dog-breeds__header') || root;
      if (!target || typeof target.getBoundingClientRect !== 'function') return;

      var rect = target.getBoundingClientRect();
      var pageY = window.pageYOffset || window.scrollY || 0;
      var top = Math.max(0, rect.top + pageY - 18);

      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      try {
        window.scrollTo({ top: top, behavior: behavior });
      } catch (e) {
        window.scrollTo(0, top);
      }
    }

    // ---- Mobile filters open/close ----
    function openFilters() {
      if (!filtersPanel) return;
      root.classList.add('db-filters-open');
      if (overlay) overlay.hidden = false;
      if (openFiltersBtn) openFiltersBtn.setAttribute('aria-expanded', 'true');

      document.documentElement.classList.add('db-no-scroll');
      document.body.classList.add('db-no-scroll');

      window.setTimeout(function () {
        var focusTarget = filtersPanel.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusTarget && typeof focusTarget.focus === 'function') {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (e) {
            focusTarget.focus();
          }
        }
      }, 30);
    }

    function closeFilters(opts) {
      opts = opts || {};

      root.classList.remove('db-filters-open');
      if (overlay) overlay.hidden = true;
      if (openFiltersBtn) openFiltersBtn.setAttribute('aria-expanded', 'false');

      document.documentElement.classList.remove('db-no-scroll');
      document.body.classList.remove('db-no-scroll');

      if (opts.restoreFocus === false) return;

      if (openFiltersBtn && typeof openFiltersBtn.focus === 'function') {
        try {
          openFiltersBtn.focus({ preventScroll: true });
        } catch (e) {
          openFiltersBtn.focus();
        }
      }
    }

    if (openFiltersBtn) {
      openFiltersBtn.addEventListener('click', function () {
        openFilters();
      });
    }

    closeFiltersBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeFilters();
      });
    });

    if (overlay) {
      overlay.addEventListener('click', function () {
        closeFilters();
      });
    }

    function getFocusableFilterEls() {
      if (!filtersPanel) return [];
      return Array.prototype.slice
        .call(filtersPanel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) {
          return !!el && !el.disabled && el.offsetParent !== null;
        });
    }

    function trapFilterFocus(e) {
      if (!filtersPanel || !root.classList.contains('db-filters-open')) return;
      if (e.key !== 'Tab') return;

      var focusable = getFocusableFilterEls();
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('db-filters-open')) {
        closeFilters();
        return;
      }
      trapFilterFocus(e);
    });

    // ---- Floating quick actions + back-to-top ----
    var floatingActions = root.querySelector('[data-db-floating-actions]');
    var floatingFiltersBtn = root.querySelector('[data-db-floating-filters]');
    var floatingTopBtn = root.querySelector('[data-db-floating-top]');
    var backToTopBtn = root.querySelector('[data-db-back-to-top]');

    function isMobileView() {
      if (!('matchMedia' in window)) return true;
      return window.matchMedia('(max-width: 989px)').matches;
    }

    function shouldShowFloatingActions() {
      if (!floatingActions) return false;
      if (!openFiltersBtn) return false;
      if (!isMobileView()) return false;

      var r = openFiltersBtn.getBoundingClientRect();
      return r.bottom <= 0;
    }

    function shouldShowBackToTop() {
      if (!backToTopBtn) return false;
      var pageY = window.pageYOffset || window.scrollY || 0;
      var rootTop = root.getBoundingClientRect().top + pageY;
      return pageY > rootTop + 420;
    }

    function setFloatingVisible(show) {
      if (!floatingActions) return;
      floatingActions.hidden = !show;
    }

    function setBackToTopVisible(show) {
      if (!backToTopBtn) return;
      backToTopBtn.hidden = !show;
    }

    var floatRaf = 0;
    function updateFloatingActions() {
      floatRaf = 0;
      setFloatingVisible(shouldShowFloatingActions());
      setBackToTopVisible(shouldShowBackToTop());
    }

    function scheduleFloatingUpdate() {
      if (!floatingActions && !backToTopBtn) return;
      if (floatRaf) return;
      floatRaf = window.requestAnimationFrame(updateFloatingActions);
    }

    if (floatingFiltersBtn) {
      floatingFiltersBtn.addEventListener('click', function () {
        if (openFiltersBtn) {
          openFiltersBtn.click();
        } else {
          openFilters();
        }
      });
    }

    if (floatingTopBtn) {
      floatingTopBtn.addEventListener('click', function () {
        scrollToHubTop();
      });
    }

    if (backToTopBtn) {
      backToTopBtn.addEventListener('click', function () {
        scrollToHubTop();
      });
    }

    window.addEventListener('scroll', scheduleFloatingUpdate, { passive: true });
    window.addEventListener('resize', function () {
      scheduleFloatingUpdate();
      if (!isMobileView() && root.classList.contains('db-filters-open')) {
        closeFilters({ restoreFocus: false });
      }
    });
    scheduleFloatingUpdate();

    // ---- Accordion: only one open at a time ----
    function scrollDetailsIntoView(det) {
      if (!filtersBody || !det || typeof det.getBoundingClientRect !== 'function') return;

      var detRect = det.getBoundingClientRect();
      var bodyRect = filtersBody.getBoundingClientRect();

      var overTop = detRect.top < bodyRect.top;
      var overBottom = detRect.bottom > bodyRect.bottom;

      if (!overTop && !overBottom) return;

      var delta = overTop ? detRect.top - bodyRect.top : detRect.bottom - bodyRect.bottom;

      try {
        filtersBody.scrollTo({ top: filtersBody.scrollTop + delta, behavior: 'smooth' });
      } catch (err) {
        filtersBody.scrollTop = filtersBody.scrollTop + delta;
      }
    }

    var accordionDetails = [];
    if (filtersPanel) {
      accordionDetails = Array.prototype.slice.call(filtersPanel.querySelectorAll('details[data-db-accordion]'));
      accordionDetails.forEach(function (det) {
        det.addEventListener('toggle', function () {
          if (!det.open) return;
          accordionDetails.forEach(function (other) {
            if (other !== det) other.open = false;
          });
          scrollDetailsIntoView(det);
        });
      });
    }

    // ---- Range filtering helpers ----
    function collectSelectedRanges() {
      if (!allFilterInputs || !allFilterInputs.length) return {};

      var selected = {};

      allFilterInputs.forEach(function (inp) {
        if (!inp || !inp.checked) return;

        var g = inp._dbGroup || (inp.getAttribute('data-db-filter-group') || '').trim();
        if (!g) return;

        var min = inp._dbMin != null ? inp._dbMin : toNumber(inp.getAttribute('data-min'));
        var max = inp._dbMax != null ? inp._dbMax : toNumber(inp.getAttribute('data-max'));
        if (min == null || max == null) return;

        if (!selected[g]) selected[g] = [];
        selected[g].push({
          min: min,
          max: max,
          minExclusive: !!inp._dbMinExclusive,
          el: inp,
        });
      });

      return selected;
    }

    function matchesRanges(value, ranges) {
      if (!ranges || !ranges.length) return true;
      if (value == null) return false;
      return ranges.some(function (r) {
        var aboveMin = r.minExclusive ? value > r.min : value >= r.min;
        return aboveMin && value <= r.max;
      });
    }

    function getOptionText(inputEl) {
      if (!inputEl) return '';
      var label = inputEl.closest('label');
      if (!label) return '';
      var textEl = label.querySelector('.db-option__text');
      if (textEl) return (textEl.textContent || '').trim();
      return (label.textContent || '').trim();
    }

    function getGroupLabel(inputEl) {
      if (!inputEl) return '';
      var det = inputEl.closest('details');
      if (!det) return '';
      var labelEl = det.querySelector('.db-filter__label');
      return labelEl ? (labelEl.textContent || '').trim() : '';
    }

    function matchesSearchQuery(breed, q) {
      if (!q) return true;
      return breed.titleNorm.indexOf(q) !== -1 || breed.excerptNorm.indexOf(q) !== -1;
    }

    function getBreedValueForGroup(breed, group) {
      var key = FILTER_VALUE_KEYS[group];
      return key ? breed[key] : null;
    }

    function inputToRange(inputEl) {
      if (!inputEl) return null;
      var min = inputEl._dbMin != null ? inputEl._dbMin : toNumber(inputEl.getAttribute('data-min'));
      var max = inputEl._dbMax != null ? inputEl._dbMax : toNumber(inputEl.getAttribute('data-max'));
      if (min == null || max == null) return null;
      return {
        min: min,
        max: max,
        minExclusive: !!inputEl._dbMinExclusive,
        el: inputEl,
      };
    }

    function matchesSelectedRangesExcept(breed, selectedRanges, exceptGroup) {
      if (!selectedRanges) return true;

      var groups = Object.keys(selectedRanges);
      for (var i = 0; i < groups.length; i++) {
        var group = groups[i];
        if (group === exceptGroup) continue;

        var ranges = selectedRanges[group];
        if (!ranges || !ranges.length) continue;

        if (!matchesRanges(getBreedValueForGroup(breed, group), ranges)) return false;
      }

      return true;
    }

    function ensureOptionCountEl(inputEl) {
      if (!inputEl || !inputEl.closest) return null;
      var label = inputEl.closest('label');
      if (!label) return null;

      var countEl = label.querySelector('.db-option__count');
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = 'db-option__count';
        countEl.setAttribute('aria-hidden', 'true');
        countEl.hidden = true;
        label.appendChild(countEl);
      }
      return countEl;
    }

    function updateOptionAvailability(q, selectedRanges) {
      if (!allFilterInputs || !allFilterInputs.length) return;

      var dataPending = hasMoreServerData();

      allFilterInputs.forEach(function (inputEl) {
        if (!inputEl) return;

        var label = inputEl.closest ? inputEl.closest('label') : null;
        var countEl = ensureOptionCountEl(inputEl);

        if (dataPending) {
          inputEl.disabled = false;
          if (label) {
            label.classList.remove('is-unavailable');
            label.removeAttribute('aria-disabled');
          }
          if (countEl) countEl.hidden = true;
          return;
        }

        var group = inputEl._dbGroup || (inputEl.getAttribute('data-db-filter-group') || '').trim();
        var optionRange = inputToRange(inputEl);
        var count = 0;

        if (group && optionRange) {
          breeds.forEach(function (breed) {
            if (!matchesSearchQuery(breed, q)) return;
            if (!matchesSelectedRangesExcept(breed, selectedRanges, group)) return;
            if (!matchesRanges(getBreedValueForGroup(breed, group), [optionRange])) return;
            count++;
          });
        }

        if (countEl) {
          countEl.textContent = String(count);
          countEl.hidden = false;
        }

        var unavailable = !inputEl.checked && count === 0;
        inputEl.disabled = unavailable;

        if (label) {
          label.classList.toggle('is-unavailable', unavailable);
          if (unavailable) label.setAttribute('aria-disabled', 'true');
          else label.removeAttribute('aria-disabled');
        }
      });
    }

    function ensureSummaryCountEl(detailsEl) {
      if (!detailsEl) return null;
      var summary = detailsEl.querySelector('.db-filter__summary');
      if (!summary) return null;

      var badge = summary.querySelector('.db-filter__selected-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'db-filter__selected-count';
        badge.hidden = true;
        var chev = summary.querySelector('.db-filter__chev');
        if (chev) summary.insertBefore(badge, chev);
        else summary.appendChild(badge);
      }
      return badge;
    }

    function updateFilterSummaryStates() {
      if (!accordionDetails || !accordionDetails.length) return;

      accordionDetails.forEach(function (det) {
        var selectedCount = Array.prototype.slice
          .call(det.querySelectorAll('input[type="checkbox"][data-db-filter-group]:checked'))
          .length;
        var badge = ensureSummaryCountEl(det);

        det.classList.toggle('has-active-options', selectedCount > 0);

        if (!badge) return;
        if (selectedCount > 0) {
          badge.textContent = String(selectedCount);
          badge.hidden = false;
          badge.setAttribute('aria-label', selectedCount + ' selected');
        } else {
          badge.hidden = true;
          badge.removeAttribute('aria-label');
        }
      });
    }

    // ---- Active chips ----
    function buildChips(applyFn) {
      if (!activeWrap || !activeList || !filtersPanel) return;

      var chips = [];

      var checked = Array.prototype.slice.call(
        filtersPanel.querySelectorAll('input[type="checkbox"][data-db-filter-group]:checked')
      );

      checked.forEach(function (el) {
        var groupLabel = getGroupLabel(el);
        var optText = getOptionText(el);
        var label = groupLabel ? groupLabel + ': ' + optText : optText;
        chips.push({ type: 'checkbox', label: label, el: el });
      });

      var q = normalize(input ? input.value : '');
      if (q) {
        chips.push({ type: 'search', label: 'Search: “' + q + '”' });
      }

      activeList.innerHTML = '';

      if (!chips.length) {
        activeWrap.hidden = true;
        return;
      }

      activeWrap.hidden = false;

      chips.forEach(function (chip) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'db-chip';
        btn.setAttribute('data-db-chip', chip.type);
        btn.innerHTML = '<span class="db-chip__text"></span><span class="db-chip__x" aria-hidden="true">×</span>';
        btn.querySelector('.db-chip__text').textContent = chip.label;

        btn.addEventListener('click', function () {
          if (chip.type === 'checkbox' && chip.el) {
            chip.el.checked = false;
            currentPage = 1;
            queueApply({ preserveScroll: true });
            return;
          }
          if (chip.type === 'search' && input) {
            input.value = '';
            if (clearSearchBtn) clearSearchBtn.hidden = true;
            currentPage = 1;
            queueApply({ preserveScroll: true });
            return;
          }
        });

        activeList.appendChild(btn);
      });
    }

    function getRenderedCards() {
      return Array.prototype.slice.call(grid.querySelectorAll('[data-db-card]'));
    }

    function findAnchorCardEl() {
      var rendered = getRenderedCards();
      if (!rendered.length) return null;

      var best = null;
      var bestTop = -Infinity;

      rendered.forEach(function (card) {
        var r = card.getBoundingClientRect();
        if (r.bottom <= 0) return;

        if (r.top <= 0 && r.bottom > 0) {
          if (r.top > bestTop) {
            bestTop = r.top;
            best = card;
          }
        }
      });

      if (best) return best;

      var minTop = Infinity;
      rendered.forEach(function (card) {
        var r = card.getBoundingClientRect();
        if (r.top >= 0 && r.top < minTop) {
          minTop = r.top;
          best = card;
        }
      });

      return best;
    }

    // ---- URL state helpers ----
    function urlHasAnyDbState(url) {
      if (!url || !url.searchParams) return false;
      var params = url.searchParams;
      if (params.has(PARAMS.page) || params.has(PARAMS.per) || params.has(PARAMS.q)) return true;
      for (var i = 0; i < filterGroupNames.length; i++) {
        if (params.has(PARAMS.filterPrefix + filterGroupNames[i])) return true;
      }
      return false;
    }

    function readStateFromUrl() {
      var state = {
        page: 1,
        per: defaultPerPage,
        perMode: 'number',
        q: '',
        filters: {},
        hadAny: false,
      };

      if (!('URL' in window)) return state;

      try {
        var url = new URL(window.location.href);
        state.hadAny = urlHasAnyDbState(url);

        var params = url.searchParams;
        var perRaw = params.get(PARAMS.per);
        if (String(perRaw || '').toLowerCase() === 'all' && hasAllPerPageOption) {
          state.per = 'all';
          state.perMode = 'all';
        } else {
          var perParsed = parseInt(perRaw || '', 10);
          if (isFinite(perParsed) && perParsed > 0) state.per = perParsed;
        }

        var pageRaw = params.get(PARAMS.page);
        var pageParsed = parseInt(pageRaw || '', 10);
        if (isFinite(pageParsed) && pageParsed > 0) state.page = pageParsed;

        var qRaw = params.get(PARAMS.q);
        if (qRaw != null) state.q = String(qRaw);

        filterGroupNames.forEach(function (g) {
          var key = PARAMS.filterPrefix + g;
          var raw = params.get(key);
          if (!raw) return;
          var toks = raw
            .split(',')
            .map(function (s) {
              return (s || '').trim();
            })
            .filter(Boolean);
          if (toks.length) state.filters[g] = toks;
        });
      } catch (e) {}

      return state;
    }

    function writeStateToUrl(opts) {
      opts = opts || {};
      if (!('URL' in window) || !('history' in window)) return;

      var q = input ? String(input.value || '').trim() : '';
      var selected = {};

      filterGroupNames.forEach(function (g) {
        var arr = [];
        var inputs = groupInputs[g] || [];
        inputs.forEach(function (inp) {
          if (!inp.checked) return;
          var t = optionToken(inp);
          if (t) arr.push(t);
        });
        if (arr.length) selected[g] = arr;
      });

      var shouldWritePer = isAllPerPage() || perPage !== defaultPerPage;
      var shouldWritePage = currentPage > 1;
      var shouldWriteQ = !!q;
      var shouldWriteAnyFilter = Object.keys(selected).length > 0;

      var url;
      try {
        url = new URL(window.location.href);
      } catch (e) {
        return;
      }

      url.searchParams.delete(PARAMS.page);
      url.searchParams.delete(PARAMS.per);
      url.searchParams.delete(PARAMS.q);
      filterGroupNames.forEach(function (g) {
        url.searchParams.delete(PARAMS.filterPrefix + g);
      });

      if (shouldWriteQ) url.searchParams.set(PARAMS.q, q);
      if (shouldWritePage) url.searchParams.set(PARAMS.page, String(currentPage));

      if (shouldWritePer || shouldWritePage || shouldWriteQ || shouldWriteAnyFilter) {
        url.searchParams.set(PARAMS.per, isAllPerPage() ? 'all' : String(perPage));
      }

      Object.keys(selected).forEach(function (g) {
        url.searchParams.set(PARAMS.filterPrefix + g, selected[g].join(','));
      });

      var next = url.toString();
      var cur = window.location.href;

      if (next !== cur) {
        try {
          if (opts.replace) history.replaceState(null, '', next);
          else history.pushState(null, '', next);
        } catch (e) {}
      }

      safeSessionSet(STORAGE_KEYS.hubUrl, window.location.href);
    }

    function restoreControlsFromState(state) {
      if (!state) return;

      if (state.perMode === 'all') {
        setPerPageFromValue('all');
      } else if (state.per) {
        setPerPageFromValue(state.per);
      }
      syncPerPageSelect();

      if (input) {
        input.value = state.q || '';
        if (clearSearchBtn) clearSearchBtn.hidden = !normalize(input.value);
      }

      if (allFilterInputs.length) {
        allFilterInputs.forEach(function (inp) {
          inp.checked = false;
        });

        Object.keys(state.filters || {}).forEach(function (g) {
          var toks = state.filters[g] || [];
          toks.forEach(function (t) {
            var inp = groupTokenIndex[g] ? groupTokenIndex[g][t] : null;
            if (inp) inp.checked = true;
          });
        });
      }

      if (state.page && state.page > 0) {
        currentPage = state.page;
        pendingPage = state.page;
      }
    }

    // ---- Pagination rendering ----
    function buildPageItems(cur, total) {
      var items = [];
      if (total <= 7) {
        for (var i = 1; i <= total; i++) items.push(i);
        return items;
      }

      var start = Math.max(2, cur - 2);
      var end = Math.min(total - 1, cur + 2);

      items.push(1);
      if (start > 2) items.push('…');
      for (var p = start; p <= end; p++) items.push(p);
      if (end < total - 1) items.push('…');
      items.push(total);

      return items;
    }

    function renderPagination(totalMatches) {
      if (!pagerWrap || !pagerNav) return;

      var totalPages = Math.max(1, Math.ceil(totalMatches / perPage));
      if (totalPages <= 1 || totalMatches === 0) {
        pagerWrap.hidden = true;
        pagerNav.innerHTML = '';
        return;
      }

      pagerWrap.hidden = false;

      var displayPage = clampInt(currentPage, 1, totalPages);
      var items = buildPageItems(displayPage, totalPages);

      var html = '';
      html +=
        '<button type="button" class="db-page-btn db-page-prev" data-db-page-prev aria-label="Previous dog breed page" ' +
        (displayPage <= 1 ? 'disabled' : '') +
        '>Prev</button>';

      html += '<div class="db-page-list" role="list">';

      items.forEach(function (it) {
        if (it === '…') {
          html += '<span class="db-page-ellipsis" aria-hidden="true">…</span>';
          return;
        }
        var isCur = it === displayPage;
        html +=
          '<button type="button" class="db-page-btn db-page-num' +
          (isCur ? ' is-current' : '') +
          '" data-db-page="' +
          it +
          '" aria-label="' +
          (isCur ? 'Current dog breed page, page ' : 'Go to dog breed page ') +
          it +
          '"' +
          (isCur ? ' aria-current="page"' : '') +
          '>' +
          it +
          '</button>';
      });

      html += '</div>';

      html +=
        '<button type="button" class="db-page-btn db-page-next" data-db-page-next aria-label="Next dog breed page" ' +
        (displayPage >= totalPages ? 'disabled' : '') +
        '>Next</button>';

      pagerNav.innerHTML = html;
    }

    if (pagerNav) {
      pagerNav.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        var pageBtn = t.closest('button[data-db-page]');
        var prevBtn = t.closest('button[data-db-page-prev]');
        var nextBtn = t.closest('button[data-db-page-next]');

        if (pageBtn && pageBtn.getAttribute) {
          var nextPage = parseInt(pageBtn.getAttribute('data-db-page') || '1', 10);
          if (isFinite(nextPage) && nextPage > 0 && nextPage !== currentPage) {
            currentPage = nextPage;
            pendingPage = null;
            apply({ preserveScroll: false, pushUrl: true });
            scrollToResultsTop();
          }
          return;
        }

        if (prevBtn) {
          if (currentPage > 1) {
            currentPage = currentPage - 1;
            pendingPage = null;
            apply({ preserveScroll: false, pushUrl: true });
            scrollToResultsTop();
          }
          return;
        }

        if (nextBtn) {
          currentPage = currentPage + 1;
          pendingPage = null;
          apply({ preserveScroll: false, pushUrl: true });
          scrollToResultsTop();
          return;
        }
      });
    }

    // ---- Rendering ----
    function buildCardEl(breed) {
      var article = document.createElement('article');
      article.className = 'db-breed-card';
      article.setAttribute('data-db-card', '');
      article.setAttribute('data-db-card-href', breed.href);

      var imgHtml = '';
      if (breed.img && breed.img.src) {
        imgHtml =
          '<img class="db-breed-card__image" loading="lazy" decoding="async" src="' +
          escapeAttr(breed.img.src) +
          '" alt="' +
          escapeAttr(breed.img.alt || breed.title || '') +
          '">';
      }

      var hoverHtml = '';
      if (breed.excerpt) {
        hoverHtml =
          '<div class="db-breed-card__hover" aria-hidden="true">' +
          '<p class="db-breed-card__excerpt" data-db-excerpt>' +
          escapeHtml(breed.excerpt) +
          '</p>' +
          '</div>';
      }

      article.innerHTML =
        '<a href="' +
        escapeAttr(breed.href) +
        '" class="db-breed-card__link full-unstyled-link">' +
        '<div class="db-breed-card__media" aria-hidden="true">' +
        imgHtml +
        hoverHtml +
        '</div>' +
        '<h2 class="db-breed-card__title h4">' +
        escapeHtml(breed.title) +
        '</h2>' +
        '</a>';

      return article;
    }

    var clientGridRendered = false;

    function renderGrid(visibleBreeds, opts) {
      opts = opts || {};

      // On the default first paint, keep the Liquid-rendered cards in place.
      // This avoids replacing identical image nodes immediately after the deferred script runs.
      if (opts.reuseExisting && !clientGridRendered) {
        var existingCards = getRenderedCards();
        if (existingCards.length === (visibleBreeds || []).length) {
          clientGridRendered = true;
          return;
        }
      }

      var frag = document.createDocumentFragment();
      (visibleBreeds || []).forEach(function (b) {
        frag.appendChild(buildCardEl(b));
      });

      // Remove the SSR cards + JSON script content from inside the grid.
      if (grid.replaceChildren) {
        grid.replaceChildren(frag);
      } else {
        grid.innerHTML = '';
        grid.appendChild(frag);
      }
      clientGridRendered = true;
    }

    // ---- Main apply ----
    var lastMatches = breeds.slice();

    // Batch repeated UI events into a single apply() call per frame.
    var applyRaf = 0;
    var applyQueuedOpts = null;

    function queueApply(opts) {
      opts = opts || {};

      if (applyQueuedOpts) {
        applyQueuedOpts.preserveScroll = applyQueuedOpts.preserveScroll || !!opts.preserveScroll;
        applyQueuedOpts.pushUrl = applyQueuedOpts.pushUrl || !!opts.pushUrl;
        applyQueuedOpts.skipUrl = applyQueuedOpts.skipUrl || !!opts.skipUrl;
      } else {
        applyQueuedOpts = {
          preserveScroll: !!opts.preserveScroll,
          pushUrl: !!opts.pushUrl,
          skipUrl: !!opts.skipUrl,
        };
      }

      if (applyRaf) return;
      applyRaf = window.requestAnimationFrame(function () {
        applyRaf = 0;
        var o = applyQueuedOpts || {};
        applyQueuedOpts = null;
        apply(o);
      });
    }

    function apply(opts) {
      opts = opts || {};

      syncPerPageSelect();

      var preserve = !!opts.preserveScroll;

      var anchorHref = null;
      var anchorTop = null;
      var pageY = null;
      var bodyScroll = null;

      if (preserve) {
        var anchorEl = findAnchorCardEl();
        if (anchorEl) {
          anchorHref = (anchorEl.getAttribute('data-db-card-href') || '').trim();
          anchorTop = anchorEl.getBoundingClientRect().top;
        }
        pageY = window.pageYOffset || window.scrollY || 0;
        bodyScroll = filtersBody ? filtersBody.scrollTop : null;
      }

      var q = normalize(input ? input.value : '');

      var selectedRanges = collectSelectedRanges();

      var heightSel = selectedRanges.height || [];
      var weightSel = selectedRanges.weight || [];
      var lifeSel = selectedRanges.lifespan || [];

      var apartmentSel = selectedRanges.apartment_dog || [];
      var calmSel = selectedRanges.calmness || [];
      var groomSel = selectedRanges.grooming_clean_home || [];
      var energySel = selectedRanges.energy_activity || [];
      var familySel = selectedRanges.family_dog || [];
      var guardSel = selectedRanges.guard_watch || [];
      var smartSel = selectedRanges.smart || [];
      var childrenSel = selectedRanges.children || [];
      var multiSel = selectedRanges.multi_pet || [];
      var trainingSel = selectedRanges.training || [];

      lastMatches = [];

      breeds.forEach(function (b) {
        if (!matchesSearchQuery(b, q)) return;

        if (!matchesRanges(b.height, heightSel)) return;
        if (!matchesRanges(b.weight, weightSel)) return;
        if (!matchesRanges(b.lifespan, lifeSel)) return;

        if (!matchesRanges(b.grade_apartment_dog, apartmentSel)) return;
        if (!matchesRanges(b.grade_calmness, calmSel)) return;
        if (!matchesRanges(b.grade_grooming_clean_home, groomSel)) return;
        if (!matchesRanges(b.grade_energy_activity, energySel)) return;
        if (!matchesRanges(b.grade_family_dog, familySel)) return;
        if (!matchesRanges(b.grade_guard_watch, guardSel)) return;
        if (!matchesRanges(b.grade_smart, smartSel)) return;
        if (!matchesRanges(b.grade_children, childrenSel)) return;
        if (!matchesRanges(b.grade_multi_pet, multiSel)) return;
        if (!matchesRanges(b.grade_training, trainingSel)) return;

        lastMatches.push(b);
      });

      var hasActiveCriteria = !!q || selectedRangesHaveAny(selectedRanges);
      var fullDataPending = hasMoreServerData();

      if (hasActiveCriteria && fullDataPending) {
        kickLoadAllBreeds();
      }

      var total = lastMatches.length;
      var displayTotal = !hasActiveCriteria && serverTotal > total ? serverTotal : total;
      var totalPages = Math.max(1, Math.ceil(displayTotal / perPage));

      // If we were waiting for more server pages to load (e.g. user requested page 22
      // but we only had enough data for 21), retry now when possible.
      if (pendingPage != null && pendingPage <= totalPages) {
        currentPage = pendingPage;
        pendingPage = null;
      }

      // Clamp the current page.
      if (currentPage > totalPages) {
        pendingPage = pendingPage == null ? currentPage : pendingPage;
        currentPage = totalPages;
      }
      currentPage = clampInt(currentPage, 1, totalPages);

      var startIdx = (currentPage - 1) * perPage;
      var endIdx = startIdx + perPage;
      var visible = lastMatches.slice(startIdx, endIdx);
      var waitingForData = fullDataPending && (hasActiveCriteria || isAllPerPage() || endIdx > total);

      if (waitingForData) {
        kickLoadAllBreeds();
      }

      renderGrid(visible, {
        reuseExisting:
          !!opts.reuseExistingGrid && currentPage === 1 && !hasActiveCriteria && perPage === defaultPerPage && !isAllPerPage(),
      });

      setLoadingStatus(waitingForData, hasActiveCriteria ? 'Checking all breeds against your filters…' : 'Loading more breeds…');
      setNoMatches(total === 0 && !waitingForData);
      setCounts(displayTotal, visible.length);
      renderLoadMore(displayTotal, visible.length, waitingForData);
      renderPagination(displayTotal);

      if (clearSearchBtn && input) {
        clearSearchBtn.hidden = !q;
      }

      buildChips(queueApply);
      updateFilterSummaryStates();
      updateOptionAvailability(q, selectedRanges);
      setClearButtonsEnabled(hasActiveCriteria);
      scheduleFloatingUpdate();

      if (!opts.skipUrl) {
        var shouldPush = !!opts.pushUrl;
        writeStateToUrl({ replace: !shouldPush });
      } else {
        safeSessionSet(STORAGE_KEYS.hubUrl, window.location.href);
      }

      if (preserve) {
        window.requestAnimationFrame(function () {
          if (filtersBody && bodyScroll != null) {
            filtersBody.scrollTop = bodyScroll;
          }

          if (anchorHref && anchorTop != null) {
            var cardsNow = getRenderedCards();
            var target = null;
            for (var i = 0; i < cardsNow.length; i++) {
              var c = cardsNow[i];
              if ((c.getAttribute('data-db-card-href') || '').trim() === anchorHref) {
                target = c;
                break;
              }
            }
            if (target) {
              var newTop = target.getBoundingClientRect().top;
              var delta = newTop - anchorTop;
              if (delta) window.scrollBy(0, delta);
              return;
            }
          }

          if (pageY != null) {
            var doc = document.documentElement;
            var maxScroll = Math.max(0, (doc.scrollHeight || 0) - window.innerHeight);
            var targetY = Math.min(pageY, maxScroll);
            window.scrollTo(0, targetY);
          }
        });
      }
    }

    // ---- Scroll restoration when returning from a breed article ----
    function saveReturnPosition() {
      var urlKey = window.location.pathname + window.location.search;

      safeSessionSet(STORAGE_KEYS.hubUrl, window.location.href);
      safeSessionSet(STORAGE_KEYS.restoreFlag, '1');
      safeSessionSet(STORAGE_KEYS.restoreUrlKey, urlKey);
      safeSessionSet(STORAGE_KEYS.restoreScrollY, String(window.pageYOffset || window.scrollY || 0));

      var anchorEl = findAnchorCardEl();
      if (anchorEl) {
        var href = (anchorEl.getAttribute('data-db-card-href') || '').trim();
        if (href) safeSessionSet(STORAGE_KEYS.restoreAnchorHref, href);
        safeSessionSet(STORAGE_KEYS.restoreAnchorTop, String(anchorEl.getBoundingClientRect().top || 0));
      }
    }

    function restoreReturnPositionIfNeeded() {
      var flag = safeSessionGet(STORAGE_KEYS.restoreFlag);
      if (flag !== '1') return;

      var expectedKey = safeSessionGet(STORAGE_KEYS.restoreUrlKey) || '';
      var currentKey = window.location.pathname + window.location.search;
      if (!expectedKey || expectedKey !== currentKey) {
        safeSessionRemove(STORAGE_KEYS.restoreFlag);
        safeSessionRemove(STORAGE_KEYS.restoreUrlKey);
        safeSessionRemove(STORAGE_KEYS.restoreScrollY);
        safeSessionRemove(STORAGE_KEYS.restoreAnchorHref);
        safeSessionRemove(STORAGE_KEYS.restoreAnchorTop);
        return;
      }

      safeSessionRemove(STORAGE_KEYS.restoreFlag);
      safeSessionRemove(STORAGE_KEYS.restoreUrlKey);

      var scrollY = parseInt(safeSessionGet(STORAGE_KEYS.restoreScrollY) || '', 10);
      var anchorHref = (safeSessionGet(STORAGE_KEYS.restoreAnchorHref) || '').trim();
      var anchorTop = parseFloat(safeSessionGet(STORAGE_KEYS.restoreAnchorTop) || '');

      safeSessionRemove(STORAGE_KEYS.restoreScrollY);
      safeSessionRemove(STORAGE_KEYS.restoreAnchorHref);
      safeSessionRemove(STORAGE_KEYS.restoreAnchorTop);

      function tryAnchorScroll() {
        if (!anchorHref) return false;
        if (!isFinite(anchorTop)) return false;

        var cardsNow = getRenderedCards();
        var anchorCard = null;
        for (var i = 0; i < cardsNow.length; i++) {
          var c = cardsNow[i];
          if ((c.getAttribute('data-db-card-href') || '').trim() === anchorHref) {
            anchorCard = c;
            break;
          }
        }

        if (!anchorCard) return false;
        var newTop = anchorCard.getBoundingClientRect().top;
        var delta = newTop - anchorTop;
        if (delta) window.scrollBy(0, delta);
        return true;
      }

      window.requestAnimationFrame(function () {
        var usedAnchor = tryAnchorScroll();
        if (usedAnchor) return;
        if (isFinite(scrollY) && scrollY >= 0) window.scrollTo(0, scrollY);
      });
    }

    // Save return position when navigating to a breed article from this hub.
    grid.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var link = t.closest('a.db-breed-card__link');
      if (!link) return;
      if (!isPlainLeftClick(e)) return;
      saveReturnPosition();
    });

    // ---- Events ----
    function clearAllControls() {
      if (filtersPanel) {
        var boxes = Array.prototype.slice.call(
          filtersPanel.querySelectorAll('input[type="checkbox"][data-db-filter-group]')
        );
        boxes.forEach(function (b) {
          b.checked = false;
        });
      }

      if (input) {
        input.value = '';
        if (clearSearchBtn) clearSearchBtn.hidden = true;
      }
    }

    if (perPageSelect) {
      perPageSelect.addEventListener('change', function () {
        setPerPageFromValue(perPageSelect.value || String(defaultPerPage));
        syncPerPageSelect();
        currentPage = 1;
        pendingPage = null;
        if (isAllPerPage() && hasMoreServerData()) kickLoadAllBreeds();
        queueApply({ preserveScroll: true });
      });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        var next = getNextPerPageValue();
        if (next == null) {
          if (hasMoreServerData()) kickLoadAllBreeds();
          return;
        }

        setPerPageFromValue(next);
        syncPerPageSelect();
        currentPage = 1;
        pendingPage = null;
        if (isAllPerPage() && hasMoreServerData()) kickLoadAllBreeds();
        queueApply({ preserveScroll: true });
      });
    }

    if (input) {
      var searchTmr = 0;
      input.addEventListener('input', function () {
        currentPage = 1;
        pendingPage = null;

        if (searchTmr) window.clearTimeout(searchTmr);
        searchTmr = window.setTimeout(function () {
          queueApply({ preserveScroll: true });
        }, 120);
      });
    }

    if (clearSearchBtn && input) {
      clearSearchBtn.addEventListener('click', function () {
        input.value = '';
        currentPage = 1;
        pendingPage = null;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          input.focus();
        }
      });
    }

    if (filtersPanel) {
      filtersPanel.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.matches) return;
        if (t.matches('input[type="checkbox"][data-db-filter-group]')) {
          currentPage = 1;
          pendingPage = null;
          queueApply({ preserveScroll: true });
        }
      });
    }

    clearFiltersBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearAllControls();
        currentPage = 1;
        pendingPage = null;
        queueApply({ preserveScroll: true });
      });
    });

    // Respond to back/forward when our params are present.
    window.addEventListener('popstate', function () {
      var state = readStateFromUrl();
      restoreControlsFromState(state);
      apply({ preserveScroll: false, skipUrl: true });
    });

    // ---- Load additional server pages after the initial data batch ----
    function parseRemoteBreedsFromHtml(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var remoteRoot = remoteRootId ? doc.getElementById(remoteRootId) : null;
      if (!remoteRoot) remoteRoot = doc.querySelector('[data-dog-breeds-hub]');
      if (!remoteRoot) return [];

      var el = remoteRoot.querySelector('[data-db-breeds-json]');
      if (!el) return [];

      try {
        var arr = JSON.parse(el.textContent || '[]');
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    }

    function fetchServerPageData(pageNum) {
      var url;
      try {
        url = new URL(window.location.href);
      } catch (e) {
        var path = window.location.pathname || '';
        var joiner = path.indexOf('?') === -1 ? '?' : '&';
        return fetch(path + joiner + 'page=' + pageNum, { credentials: 'same-origin' })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.text();
          })
          .then(parseRemoteBreedsFromHtml);
      }

      url.searchParams.set('page', String(pageNum));
      url.searchParams.delete('sections');
      url.searchParams.delete(PARAMS.page);
      url.searchParams.delete(PARAMS.per);
      url.searchParams.delete(PARAMS.q);

      var filterParamKeys = [];
      url.searchParams.forEach(function (_value, key) {
        if (key.indexOf(PARAMS.filterPrefix) === 0) filterParamKeys.push(key);
      });
      filterParamKeys.forEach(function (key) {
        url.searchParams.delete(key);
      });

      if (sectionRenderId) {
        url.searchParams.set('section_id', sectionRenderId);
      } else {
        url.searchParams.delete('section_id');
      }

      return fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(parseRemoteBreedsFromHtml);
    }

    function loadAllBreedsAcrossPages() {
      if (serverTotalPages <= 1) return Promise.resolve();

      var pagesToLoad = [];
      for (var p = 1; p <= serverTotalPages; p++) {
        if (p === serverCurrentPage) continue;
        pagesToLoad.push(p);
      }
      if (!pagesToLoad.length) return Promise.resolve();

      var fetches = pagesToLoad.map(function (pageNum) {
        return fetchServerPageData(pageNum)
          .then(function (arr) {
            return arr || [];
          })
          .catch(function (err) {
            console.warn('[DB] Failed to load page ' + pageNum + ':', err);
            return [];
          });
      });

      return Promise.all(fetches).then(function (results) {
        if (abortServerLoad) return;

        var merged = [];
        results.forEach(function (arr) {
          if (arr && arr.length) merged = merged.concat(arr);
        });

        if (!merged.length) return;

        var added = ingestBreeds(merged);
        if (!added) return;

        sortBreeds();

        // Re-apply now that we have the expanded dataset.
        apply({ preserveScroll: true, skipUrl: true });
      });
    }

    // Abort async loads in the theme editor when the section is reloaded/removed.
    if (sectionRenderId && 'addEventListener' in document) {
      document.addEventListener('shopify:section:unload', function (event) {
        if (!event || !event.detail) return;
        if (String(event.detail.sectionId) === String(sectionRenderId)) abortServerLoad = true;
      });
    }

    // ---- Initial render ----
    var initialState = readStateFromUrl();
    if (initialState && initialState.hadAny) {
      restoreControlsFromState(initialState);
      apply({ preserveScroll: false });
    } else {
      apply({ preserveScroll: false, skipUrl: true, reuseExistingGrid: true });
    }

    // Load additional server pages after first paint.
    // If the user is returning from a breed article, prioritize loading immediately so
    // the previously-visible card exists for anchor-based scroll restoration.
    var wantsRestore = safeSessionGet(STORAGE_KEYS.restoreFlag) === '1';

    if (serverTotalPages > 1) {
      if (wantsRestore) {
        kickLoadAllBreeds();
      } else if ('requestIdleCallback' in window) {
        window.requestIdleCallback(kickLoadAllBreeds, { timeout: 400 });
      } else {
        window.setTimeout(kickLoadAllBreeds, 120);
      }
    }

    if (wantsRestore && loadPromise && typeof loadPromise.then === 'function') {
      loadPromise.then(function () {
        restoreReturnPositionIfNeeded();
      });
    } else {
      restoreReturnPositionIfNeeded();
    }
  });
})();
